import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Plus, Edit2, Search, Truck, Phone, DollarSign, History, 
    ShoppingCart, CreditCard, Eye, ArrowDownCircle, ArrowUpCircle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialProveedorForm = {
    nombre: '',
    telefono: '',
    direccion: '',
    tipo_insumo: ''
};

export const Proveedores = () => {
    const [proveedores, setProveedores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [proveedorModalOpen, setProveedorModalOpen] = useState(false);
    const [compraModalOpen, setCompraModalOpen] = useState(false);
    const [abonoModalOpen, setAbonoModalOpen] = useState(false);
    const [historialModalOpen, setHistorialModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [proveedorForm, setProveedorForm] = useState(initialProveedorForm);
    const [selectedProveedor, setSelectedProveedor] = useState(null);
    const [historial, setHistorial] = useState([]);

    const [compraForm, setCompraForm] = useState({
        fecha: new Date().toISOString().split('T')[0],
        concepto: '',
        cantidad: '',
        precio_total: 0,
        abono_inicial: 0
    });

    const [abonoForm, setAbonoForm] = useState({
        fecha: new Date().toISOString().split('T')[0],
        monto: 0,
        forma_pago: 'efectivo',
        observaciones: ''
    });

    useEffect(() => {
        fetchProveedores();
    }, []);

    const fetchProveedores = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/proveedores`);
            setProveedores(response.data);
        } catch (error) {
            console.error('Error fetching proveedores:', error);
            toast.error('Error al cargar proveedores');
        } finally {
            setLoading(false);
        }
    };

    const handleProveedorSubmit = async (e) => {
        e.preventDefault();
        if (!proveedorForm.nombre || !proveedorForm.tipo_insumo) {
            toast.error('Nombre y tipo de insumo son requeridos');
            return;
        }
        try {
            if (editingId) {
                await axios.put(`${API_URL}/api/proveedores/${editingId}`, proveedorForm);
                toast.success('Proveedor actualizado');
            } else {
                await axios.post(`${API_URL}/api/proveedores`, proveedorForm);
                toast.success('Proveedor creado');
            }
            setProveedorModalOpen(false);
            setProveedorForm(initialProveedorForm);
            setEditingId(null);
            fetchProveedores();
        } catch (error) {
            toast.error('Error al guardar proveedor');
        }
    };

    const handleCompraSubmit = async (e) => {
        e.preventDefault();
        if (!compraForm.concepto || !compraForm.precio_total || compraForm.precio_total <= 0) {
            toast.error('Ingrese concepto y monto de la compra');
            return;
        }
        try {
            await axios.post(`${API_URL}/api/proveedores/${selectedProveedor.id}/compra`, compraForm);
            toast.success('Compra registrada - deuda actualizada');
            setCompraModalOpen(false);
            setCompraForm({
                fecha: new Date().toISOString().split('T')[0],
                concepto: '', cantidad: '', precio_total: 0, abono_inicial: 0
            });
            fetchProveedores();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar compra');
        }
    };

    const handleAbonoSubmit = async (e) => {
        e.preventDefault();
        if (!abonoForm.monto || abonoForm.monto <= 0) {
            toast.error('Ingrese un monto válido');
            return;
        }
        try {
            await axios.post(`${API_URL}/api/proveedores/${selectedProveedor.id}/abono`, abonoForm);
            toast.success('Abono registrado - saldo actualizado');
            setAbonoModalOpen(false);
            setAbonoForm({
                fecha: new Date().toISOString().split('T')[0],
                monto: 0, forma_pago: 'efectivo', observaciones: ''
            });
            fetchProveedores();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar abono');
        }
    };

    const handleEdit = (proveedor) => {
        setProveedorForm({
            nombre: proveedor.nombre,
            telefono: proveedor.telefono,
            direccion: proveedor.direccion,
            tipo_insumo: proveedor.tipo_insumo
        });
        setEditingId(proveedor.id);
        setProveedorModalOpen(true);
    };

    const openCompraModal = (proveedor) => {
        setSelectedProveedor(proveedor);
        setCompraForm({
            fecha: new Date().toISOString().split('T')[0],
            concepto: '', cantidad: '', precio_total: 0, abono_inicial: 0
        });
        setCompraModalOpen(true);
    };

    const openAbonoModal = (proveedor) => {
        setSelectedProveedor(proveedor);
        setAbonoForm({
            fecha: new Date().toISOString().split('T')[0],
            monto: 0, forma_pago: 'efectivo', observaciones: ''
        });
        setAbonoModalOpen(true);
    };

    const openHistorialModal = async (proveedor) => {
        setSelectedProveedor(proveedor);
        try {
            const response = await axios.get(`${API_URL}/api/proveedores/${proveedor.id}/movimientos`);
            setHistorial(response.data);
            setHistorialModalOpen(true);
        } catch (error) {
            toast.error('Error al cargar movimientos');
        }
    };

    const filteredProveedores = proveedores.filter(prov =>
        (prov.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (prov.tipo_insumo || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalComprado = proveedores.reduce((sum, p) => sum + (p.total_comprado || 0), 0);
    const totalPagado = proveedores.reduce((sum, p) => sum + (p.total_pagado || 0), 0);
    const totalDeuda = proveedores.reduce((sum, p) => sum + (p.saldo_pendiente || 0), 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="proveedores-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Proveedores y Deudas
                    </h1>
                    <p className="text-zinc-400 mt-1">Gestión de proveedores, compras, deudas y abonos</p>
                </div>
                <Button
                    onClick={() => { setProveedorForm(initialProveedorForm); setEditingId(null); setProveedorModalOpen(true); }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                    data-testid="add-proveedor-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Nuevo Proveedor
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-400/10 flex items-center justify-center">
                            <Truck className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{proveedores.length}</p>
                            <p className="text-sm text-zinc-400">Proveedores</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-yellow-400/10 flex items-center justify-center">
                            <ShoppingCart className="w-6 h-6 text-[#F5C33B]" />
                        </div>
                        <div>
                            <p className="text-xl font-bold text-zinc-100">${totalComprado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            <p className="text-sm text-zinc-400">Total Comprado</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                            <CreditCard className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-xl font-bold text-emerald-400">${totalPagado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            <p className="text-sm text-zinc-400">Total Pagado</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-red-400/10 flex items-center justify-center">
                            <DollarSign className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                            <p className="text-xl font-bold text-red-400">${totalDeuda.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            <p className="text-sm text-zinc-400">Deuda Total</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search */}
            <Card className="card">
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                        <Input placeholder="Buscar por nombre o tipo de insumo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-proveedores" />
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="card">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Proveedor</TableHead>
                                    <TableHead className="table-header">Insumo</TableHead>
                                    <TableHead className="table-header">Total Comprado</TableHead>
                                    <TableHead className="table-header">Total Pagado</TableHead>
                                    <TableHead className="table-header">Saldo Pendiente</TableHead>
                                    <TableHead className="table-header text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredProveedores.length > 0 ? filteredProveedores.map((prov) => (
                                    <TableRow key={prov.id} className="table-row" data-testid={`proveedor-row-${prov.id}`}>
                                        <TableCell className="table-cell font-medium">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-amber-400/10 flex items-center justify-center shrink-0">
                                                    <Truck className="w-4 h-4 text-amber-400" />
                                                </div>
                                                <div>
                                                    <p className="font-medium">{prov.nombre}</p>
                                                    {prov.telefono && (
                                                        <p className="text-xs text-zinc-400 flex items-center gap-1">
                                                            <Phone className="w-3 h-3" />{prov.telefono}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="badge-info">{prov.tipo_insumo}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            ${(prov.total_comprado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                        <TableCell className="table-cell text-emerald-400">
                                            ${(prov.total_pagado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className={(prov.saldo_pendiente || 0) > 0 ? 'text-red-400 font-semibold text-base' : 'text-emerald-400 font-medium'}>
                                                ${(prov.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                            </span>
                                        </TableCell>
                                        <TableCell className="table-cell text-right">
                                            <div className="flex justify-end gap-1 flex-wrap">
                                                <Button variant="outline" size="sm" onClick={() => openCompraModal(prov)} className="text-red-400 border-[#FA383E] hover:bg-red-400/5" data-testid={`compra-prov-${prov.id}`}>
                                                    <ArrowDownCircle className="w-4 h-4 mr-1" />
                                                    Compra
                                                </Button>
                                                {(prov.saldo_pendiente || 0) > 0 && (
                                                    <Button size="sm" onClick={() => openAbonoModal(prov)} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`abono-prov-${prov.id}`}>
                                                        <ArrowUpCircle className="w-4 h-4 mr-1" />
                                                        Abonar
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" onClick={() => openHistorialModal(prov)} data-testid={`historial-prov-${prov.id}`}>
                                                    <History className="w-4 h-4 text-amber-400" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleEdit(prov)} data-testid={`edit-prov-${prov.id}`}>
                                                    <Edit2 className="w-4 h-4 text-zinc-400" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-zinc-400">
                                            No se encontraron proveedores
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Proveedor Modal */}
            <Dialog open={proveedorModalOpen} onOpenChange={setProveedorModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="proveedor-modal">
                    <DialogHeader>
                        <DialogTitle >{editingId ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleProveedorSubmit} className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Nombre *</Label>
                            <Input value={proveedorForm.nombre} onChange={(e) => setProveedorForm({ ...proveedorForm, nombre: e.target.value })} placeholder="Nombre del proveedor" data-testid="input-prov-nombre" />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Tipo de Insumo *</Label>
                            <Input value={proveedorForm.tipo_insumo} onChange={(e) => setProveedorForm({ ...proveedorForm, tipo_insumo: e.target.value })} placeholder="Ej: Telas, Cierres, Hilos..." data-testid="input-prov-tipo" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Teléfono</Label>
                                <Input value={proveedorForm.telefono} onChange={(e) => setProveedorForm({ ...proveedorForm, telefono: e.target.value })} placeholder="Teléfono" data-testid="input-prov-telefono" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Dirección</Label>
                                <Input value={proveedorForm.direccion} onChange={(e) => setProveedorForm({ ...proveedorForm, direccion: e.target.value })} placeholder="Dirección" data-testid="input-prov-direccion" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setProveedorModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-proveedor-btn">{editingId ? 'Actualizar' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Compra/Deuda Modal */}
            <Dialog open={compraModalOpen} onOpenChange={setCompraModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="compra-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <ShoppingCart className="w-5 h-5 inline mr-2 text-red-400" />
                            Registrar Compra / Deuda
                        </DialogTitle>
                    </DialogHeader>
                    {selectedProveedor && (
                        <div className="bg-zinc-800 p-3 rounded-lg mb-2">
                            <p className="font-medium text-zinc-100">{selectedProveedor.nombre}</p>
                            <p className="text-sm text-zinc-400">
                                Saldo actual: <span className="text-red-400 font-semibold">${(selectedProveedor.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                            </p>
                        </div>
                    )}
                    <form onSubmit={handleCompraSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha *</Label>
                                <Input type="date" value={compraForm.fecha} onChange={(e) => setCompraForm({ ...compraForm, fecha: e.target.value })} data-testid="compra-fecha" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Cantidad</Label>
                                <Input value={compraForm.cantidad} onChange={(e) => setCompraForm({ ...compraForm, cantidad: e.target.value })} placeholder="Ej: 50 metros" data-testid="compra-cantidad" />
                            </div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Concepto / Descripción *</Label>
                            <Input value={compraForm.concepto} onChange={(e) => setCompraForm({ ...compraForm, concepto: e.target.value })} placeholder="Ej: 50 metros mezclilla azul" data-testid="compra-concepto" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Monto Total de la Compra *</Label>
                                <Input type="number" step="0.01" min="0.01" value={compraForm.precio_total} onChange={(e) => setCompraForm({ ...compraForm, precio_total: parseFloat(e.target.value) || 0 })} placeholder="0.00" data-testid="compra-monto" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Abono Inicial</Label>
                                <Input type="number" step="0.01" min="0" value={compraForm.abono_inicial} onChange={(e) => setCompraForm({ ...compraForm, abono_inicial: parseFloat(e.target.value) || 0 })} placeholder="0.00" data-testid="compra-abono" />
                            </div>
                        </div>
                        {compraForm.precio_total > 0 && (
                            <div className="bg-red-400/5 p-3 rounded-lg border border-red-400/20">
                                <p className="text-sm text-zinc-400">Deuda que se genera:</p>
                                <p className="text-lg font-bold text-red-400">
                                    ${(compraForm.precio_total - (compraForm.abono_inicial || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setCompraModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="save-compra-btn">
                                <ShoppingCart className="w-4 h-4 mr-2" />
                                Registrar Compra
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Abono Modal */}
            <Dialog open={abonoModalOpen} onOpenChange={setAbonoModalOpen}>
                <DialogContent className="sm:max-w-[450px]" data-testid="abono-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <DollarSign className="w-5 h-5 inline mr-2 text-emerald-400" />
                            Registrar Abono / Pago
                        </DialogTitle>
                    </DialogHeader>
                    {selectedProveedor && (
                        <div className="bg-red-400/5 p-3 rounded-lg border border-red-400/20 mb-2">
                            <p className="font-medium text-zinc-100">{selectedProveedor.nombre}</p>
                            <p className="text-sm text-zinc-400">Deuda pendiente:</p>
                            <p className="text-xl font-bold text-red-400">
                                ${(selectedProveedor.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </p>
                        </div>
                    )}
                    <form onSubmit={handleAbonoSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha *</Label>
                                <Input type="date" value={abonoForm.fecha} onChange={(e) => setAbonoForm({ ...abonoForm, fecha: e.target.value })} data-testid="abono-fecha" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Monto del Abono *</Label>
                                <Input type="number" step="0.01" min="0.01" value={abonoForm.monto} onChange={(e) => setAbonoForm({ ...abonoForm, monto: parseFloat(e.target.value) || 0 })} placeholder="0.00" data-testid="abono-monto" />
                            </div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Forma de Pago</Label>
                            <Select value={abonoForm.forma_pago} onValueChange={(v) => setAbonoForm({ ...abonoForm, forma_pago: v })}>
                                <SelectTrigger data-testid="abono-forma">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="efectivo">Efectivo</SelectItem>
                                    <SelectItem value="transferencia">Transferencia</SelectItem>
                                    <SelectItem value="cheque">Cheque</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Textarea value={abonoForm.observaciones} onChange={(e) => setAbonoForm({ ...abonoForm, observaciones: e.target.value })} placeholder="Notas del pago" rows={2} data-testid="abono-observaciones" />
                        </div>
                        {abonoForm.monto > 0 && selectedProveedor && (
                            <div className="bg-emerald-400/5 p-3 rounded-lg border border-emerald-400/20">
                                <p className="text-sm text-zinc-400">Saldo después del abono:</p>
                                <p className="text-lg font-bold text-emerald-400">
                                    ${Math.max(0, (selectedProveedor.saldo_pendiente || 0) - abonoForm.monto).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setAbonoModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-abono-btn">
                                <DollarSign className="w-4 h-4 mr-2" />
                                Registrar Abono
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Historial Modal */}
            <Dialog open={historialModalOpen} onOpenChange={setHistorialModalOpen}>
                <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto" data-testid="historial-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <History className="w-5 h-5 inline mr-2 text-amber-400" />
                            Movimientos - {selectedProveedor?.nombre}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedProveedor && (
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-yellow-400/5 p-3 rounded-lg text-center">
                                <p className="text-xs text-zinc-400">Total Comprado</p>
                                <p className="text-base font-bold text-zinc-100">${(selectedProveedor.total_comprado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            </div>
                            <div className="bg-emerald-400/5 p-3 rounded-lg text-center">
                                <p className="text-xs text-zinc-400">Total Pagado</p>
                                <p className="text-base font-bold text-emerald-400">${(selectedProveedor.total_pagado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            </div>
                            <div className="bg-red-400/5 p-3 rounded-lg text-center">
                                <p className="text-xs text-zinc-400">Saldo Pendiente</p>
                                <p className="text-base font-bold text-red-400">${(selectedProveedor.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            </div>
                        </div>
                    )}
                    <div className="overflow-x-auto">
                        {historial.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-zinc-800">
                                        <TableHead className="table-header">Fecha</TableHead>
                                        <TableHead className="table-header">Tipo</TableHead>
                                        <TableHead className="table-header">Concepto</TableHead>
                                        <TableHead className="table-header text-right">Monto</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {historial.map((mov) => (
                                        <TableRow key={mov.id} className="table-row">
                                            <TableCell className="table-cell">{mov.fecha}</TableCell>
                                            <TableCell className="table-cell">
                                                {mov.tipo === 'compra' ? (
                                                    <span className="badge-error flex items-center gap-1 w-fit">
                                                        <ArrowDownCircle className="w-3 h-3" /> Compra
                                                    </span>
                                                ) : (
                                                    <span className="badge-success flex items-center gap-1 w-fit">
                                                        <ArrowUpCircle className="w-3 h-3" /> Abono
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="table-cell">
                                                {mov.concepto || mov.observaciones || mov.forma_pago || '-'}
                                            </TableCell>
                                            <TableCell className="table-cell text-right">
                                                <span className={mov.tipo === 'compra' ? 'text-red-400 font-semibold' : 'text-emerald-400 font-semibold'}>
                                                    {mov.tipo === 'compra' ? '+' : '-'}${(mov.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <div className="text-center py-8 text-zinc-400">No hay movimientos registrados</div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
